import { useState, useEffect } from "react";

export default function HeroSection() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-sm border-b border-gray-100" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-[70px]">
          <a href="https://fastpassportcenter.com" className="flex items-center">
            <img
              src="https://staging.fastpassportcenter.com/wp-content/uploads/2026/02/Header.svg"
              alt="Fast Passport Center"
              className={`h-9 w-auto transition-all ${scrolled ? "" : "brightness-0 invert"}`}
            />
          </a>
          <div className="hidden md:flex items-center gap-8">
            {[
              { label: "How It Works", href: "#how-it-works" },
              { label: "Who Qualifies", href: "#who-qualifies" },
              { label: "Emergency Tiers", href: "#emergency-tiers" },
              { label: "FAQ", href: "#faq" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  scrolled ? "text-[#1A365D] hover:text-[#DC2626]" : "text-white/90 hover:text-white"
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <a
              href="tel:8772530084"
              className={`hidden md:inline-flex items-center gap-1.5 text-sm font-bold transition-colors whitespace-nowrap ${
                scrolled ? "text-[#DC2626] hover:text-[#B91C1C]" : "text-white hover:text-white/80"
              }`}
            >
              <i className="ri-phone-line"></i>877-253-0084
            </a>
            <a
              href="https://fastpassportcenter.com/checkout/"
              className="bg-white hover:bg-gray-100 text-[#991B1B] text-sm font-bold px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
            >
              Start Emergency Order
            </a>
            <button
              className="md:hidden flex items-center justify-center w-9 h-9"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl ${scrolled ? "text-[#1A365D]" : "text-white"}`}></i>
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-3">
            {[
              { label: "How It Works", href: "#how-it-works" },
              { label: "Who Qualifies", href: "#who-qualifies" },
              { label: "Emergency Tiers", href: "#emergency-tiers" },
              { label: "FAQ", href: "#faq" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="block text-sm font-medium text-[#1A365D] hover:text-[#DC2626] py-1"
              >
                {item.label}
              </a>
            ))}
            <a href="tel:8772530084" className="block text-sm font-bold text-[#DC2626] py-1">
              <i className="ri-phone-line mr-1.5"></i>877-253-0084
            </a>
          </div>
        )}
      </nav>

      <section className="relative min-h-[650px] md:min-h-[750px] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#991B1B] via-[#7F1D1D] to-[#0F172A]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-red-500/10 blur-3xl -translate-y-1/2 translate-x-1/3"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-amber-500/8 blur-3xl translate-y-1/2 -translate-x-1/4"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[2px] bg-gradient-to-r from-transparent via-red-400/20 to-transparent rotate-12"></div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-24 pb-16 text-center">
          <p className="text-red-400 text-xs font-bold uppercase tracking-[0.25em] mb-5">
            Registered U.S. Department of State Courier
          </p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-5 leading-tight">
            Emergency Passport —{" "}
            <span className="text-red-400">As Fast as 24 Hours</span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-3xl mx-auto mb-9 leading-relaxed">
            If your trip is in hours or days and you don't have a valid passport, you need a registered courier with direct daily access to federal passport agencies. Fast Passport Center has been solving passport emergencies for over 20 years. Call us now — we answer fast.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <a
              href="tel:8772530084"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold text-lg px-10 py-5 rounded-md transition-all hover:scale-105 whitespace-nowrap shadow-lg shadow-red-900/40"
            >
              <i className="ri-phone-line text-xl"></i> Call 877-253-0084 Now
            </a>
            <a
              href="https://fastpassportcenter.com/checkout/"
              className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white hover:text-white font-bold text-lg px-10 py-5 rounded-md transition-all whitespace-nowrap"
            >
              <i className="ri-shopping-cart-line"></i> Start Emergency Order Online
            </a>
          </div>

          <p className="text-white/50 text-sm mb-10 max-w-2xl mx-auto">
            <span className="text-amber-400 font-semibold">Available Monday–Friday 8AM–6PM EST.</span> If your situation is outside business hours, start your order online and we'll process it first thing in the morning.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-5 md:gap-8">
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <i className="ri-shield-check-line text-red-400 text-base"></i>
              <span>Registered State Dept. Courier</span>
            </div>
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <i className="ri-calendar-check-line text-red-400 text-base"></i>
              <span>Daily Reserved Agency Access</span>
            </div>
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <i className="ri-award-line text-red-400 text-base"></i>
              <span>A+ BBB Accredited</span>
            </div>
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <i className="ri-time-line text-red-400 text-base"></i>
              <span>20+ Years Emergency Experience</span>
            </div>
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <i className="ri-user-voice-line text-red-400 text-base"></i>
              <span>Real Humans Answer the Phone</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}